/**
 * Proof links on Repair Tracking (docs/inspections-platform.md, section 10).
 *
 * On a type that asks for proof, each party outside the team who owns
 * actions gets one link to upload a photo of each repair. This lists each
 * party's latest link and lets staff copy it or send it again.
 */
import { Copy, Loader2, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  type ProofLink,
  useActionOwnerLabel,
  useInspectionProofLinks,
  useSendProofLinks,
} from "@/admin/hooks/use-inspections";
import { toastApiError } from "@/shared/lib/api-error";
import { useFormatters } from "@/shared/lib/formatting";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";

function useStatusLabel() {
  const { t } = useTranslation();
  return (status: ProofLink["status"]) => {
    switch (status) {
      case "sent":
        return t("inspections.proofLinks.statusSent", "Link sent");
      case "submitted":
        return t("inspections.proofLinks.statusSubmitted", "Proof submitted");
      case "expired":
        return t("inspections.proofLinks.statusExpired", "Link expired");
      default:
        return t("inspections.proofLinks.statusNotSent", "Not sent");
    }
  };
}

export function ProofLinksSection({
  inspectionUuid,
  canEdit,
}: {
  inspectionUuid: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const ownerLabel = useActionOwnerLabel();
  const statusLabel = useStatusLabel();
  const { data: links, isPending } = useInspectionProofLinks(inspectionUuid);
  const send = useSendProofLinks(inspectionUuid);

  const handleSend = async (owner: ProofLink["owner"]) => {
    try {
      await send.mutateAsync(owner);
      toast.success(t("inspections.proofLinks.sent", "Link sent."));
    } catch (e) {
      toastApiError(
        e,
        t("inspections.proofLinks.sendFailed", "Could not send the link."),
      );
    }
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("inspections.proofLinks.copied", "Link copied."));
    } catch {
      toast.error(
        t(
          "inspections.proofLinks.copyFailed",
          "Copy failed - open the link and copy it from the address bar.",
        ),
      );
    }
  };

  return (
    <section
      data-testid="proof-links"
      className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5"
    >
      <h3 className="text-sm font-medium text-[var(--ink)]">
        {t("inspections.proofLinks.title", "Proof links")}
      </h3>
      <p className="mt-1 text-xs text-[var(--ink-3)]">
        {t(
          "inspections.proofLinks.intro",
          "Each party outside your team gets one link to upload a photo of each repair they own.",
        )}
      </p>
      {isPending ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={16} className="animate-spin text-[var(--ink-3)]" />
        </div>
      ) : (links?.length ?? 0) === 0 ? (
        <p className="mt-3 text-xs text-[var(--ink-3)]">
          {t(
            "inspections.proofLinks.empty",
            "No action is owned by a party outside your team.",
          )}
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {links?.map((link) => (
            <div
              key={link.owner}
              data-testid={`proof-link-${link.owner}`}
              className="flex flex-col gap-2 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-3 sm:flex-row sm:items-center"
            >
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-[var(--ink)]">
                    {link.recipient_name
                      ? `${ownerLabel(link.owner)} - ${link.recipient_name}`
                      : ownerLabel(link.owner)}
                  </span>
                  <Badge
                    variant={link.status === "submitted" ? "default" : "outline"}
                  >
                    {statusLabel(link.status)}
                  </Badge>
                </div>
                <p className="flex flex-wrap gap-x-3 text-[11px] text-[var(--ink-3)]">
                  <span>
                    {link.recipient_email ||
                      t("inspections.proofLinks.noEmail", "No email on file")}
                  </span>
                  {link.status === "sent" && link.expires_at && (
                    <span>
                      {t("inspections.proofLinks.worksUntil", "Works until {{date}}", {
                        date: formatDate(link.expires_at),
                      })}
                    </span>
                  )}
                  {link.status === "submitted" && link.submitted_at && (
                    <span>
                      {t("inspections.proofLinks.submittedOn", "Submitted {{date}}", {
                        date: formatDate(link.submitted_at),
                      })}
                    </span>
                  )}
                  {link.awaiting > 0 && (
                    <span>
                      {t("inspections.proofLinks.awaiting", {
                        count: link.awaiting,
                        defaultValue: "{{count}} waiting for proof",
                      })}
                    </span>
                  )}
                </p>
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-2">
                  {link.url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(link.url)}
                      className="min-h-[36px]"
                    >
                      <Copy size={13} className="mr-1.5" />
                      {t("inspections.proofLinks.copy", "Copy link")}
                    </Button>
                  )}
                  {link.can_send && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSend(link.owner)}
                      disabled={send.isPending}
                      className="min-h-[36px]"
                    >
                      <Send size={13} className="mr-1.5" />
                      {link.status === "sent"
                        ? t("inspections.proofLinks.resend", "Resend")
                        : t("inspections.proofLinks.send", "Send link")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
